import { useState } from 'react';
import { Loader2, Trash2, X, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DocumentWithMetadata } from '@/types/knowledge/knowledge-base';

interface ActionButtonsProps {
  document: DocumentWithMetadata;
  hasMetadataJson: boolean;
  urlContent: string | null;
  optimizedContent: string | null;
  isOptimizing: boolean;
  isProcessing: boolean;
  isInternalAdmin: boolean;
  retryingDocuments: Set<string>;
  viewMode: "formatted" | "optimized";
  canOptimize: boolean;
  isAlreadyOptimized: boolean;
  onDeleteDocument: () => void;
  onRetryParse: () => void;
  onOptimizeDocument: (fullDocument: boolean) => void;
  onSendToKB: (document: DocumentWithMetadata) => void;
  onRemoveFromKB: (document: DocumentWithMetadata) => void;
  onClose: () => void;
}

export const ActionButtons = ({
  document,
  hasMetadataJson,
  urlContent,
  optimizedContent,
  isOptimizing,
  isProcessing,
  isInternalAdmin,
  retryingDocuments,
  viewMode,
  canOptimize,
  isAlreadyOptimized,
  onDeleteDocument,
  onRetryParse,
  onOptimizeDocument,
  onSendToKB,
  onRemoveFromKB,
  onClose,
}: ActionButtonsProps) => {
  const [isComboProcessing, setIsComboProcessing] = useState(false);

  const isDocumentTooRecent = () => {
    if (!document.created_at) return false;
    const createdTime = new Date(document.created_at).getTime();
    const now = new Date().getTime();
    const thirtyMinutesMs = 30 * 60 * 1000;
    return now - createdTime < thirtyMinutesMs;
  };

  // Handler for combined Process & Add to KB
  const handleProcessAndAddToKB = async () => {
    setIsComboProcessing(true);
    const processingToast = toast.loading("Processing document...");

    try {
      // Step 1: Optimize if needed
      if (!isAlreadyOptimized) {
        toast.loading("Optimizing document...", { id: processingToast });
        await onOptimizeDocument(true);

        // Wait a moment for optimization to complete
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Step 2: Send to KB
      toast.loading("Adding to knowledge base...", { id: processingToast });
      await onSendToKB(document);

      toast.success("✅ Document in knowledge base!", { id: processingToast });
      onClose();
    } catch (error) {
      console.error("Processing failed:", error);
      toast.error("Processing failed. Please try again.", { id: processingToast });
    } finally {
      setIsComboProcessing(false);
    }
  };

  return (
    <div className="flex justify-start p-6 border-t-2 border-gray-100 gap-2 bg-gray-50">
      {(!document.is_public || isInternalAdmin) && (
        <Button
          variant="outline"
          size="sm"
          onClick={onDeleteDocument}
          className="border-2 border-genie-secondary/30 text-genie-secondary hover:bg-genie-secondary hover:text-white hover:border-genie-secondary transition-all hover:shadow-lg hover:scale-105 rounded-lg font-semibold"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Delete
        </Button>
      )}

      {/* Remove from KB button - visible if document has el_doc_id */}
      {document.el_doc_id && (
        <Button
          variant="outline"
          size="sm"
          className="border-2 border-gray-300 text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-all hover:scale-105 rounded-lg"
          onClick={() => onRemoveFromKB(document)}
          title="Remove this document from the Knowledge Base."
        >
          <X className="h-4 w-4 mr-2" />
          Remove from KB
        </Button>
      )}

      {/* Retry parsing button - for PDF documents with source_path and completed or failed status */}
      {(document.source_type === "pdf_upload" || document.metadata?.type === "application/pdf") &&
        document.source_path &&
        (document.status === "completed" || document.status === "failed") && (
          <Button
            variant="outline"
            onClick={onRetryParse}
            disabled={retryingDocuments.has(document.id)}
            className={
              document.status === "failed"
                ? "border-orange-500 text-orange-600 hover:bg-orange-50 animate-pulse"
                : ""
            }
          >
            {retryingDocuments.has(document.id) ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Retrying...
              </>
            ) : (
              <>Retry PDF Parsing</>
            )}
          </Button>
        )}

      {/* Combo Process & Add to KB button - only shown when document can be processed */}
      {!document.el_doc_id && (hasMetadataJson || urlContent) && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleProcessAndAddToKB}
                disabled={
                  isOptimizing || isProcessing || isComboProcessing || isDocumentTooRecent()
                }
                className="bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary-hover text-white shadow-xl ring-2 ring-genie-primary/30 hover:scale-105 transition-all rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {isOptimizing || isProcessing || isComboProcessing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 mr-2" />
                    Process & Add to KB
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {isDocumentTooRecent()
                  ? "Document must be at least 30 minutes old to process"
                  : isAlreadyOptimized
                    ? "Add optimized document to knowledge base"
                    : "Optimize and add to knowledge base in one step"}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
